import React, { useEffect, useState, useCallback, useMemo } from "react";

import { createClient } from "@supabase/supabase-js";
import moment from "moment";

import "./App.css";
import { PlacingBid } from "./components/placingBid";
import { PlaceBid } from "./components/placeBid";
import { AUCTIONS, reconcileRealtimeUpdateIntoAuction } from "./utils";
import { getAuctionsByProductId, placeBid, fetchGuard } from "./lib/auctions";
import { Guard } from "./components/guard";
// import { Settlement } from "./components/settlement";

const RenderAuction = ({
  auctionGuard,
  shopId,
  productId,
  auctions,
  customerId,
  shopLoginUrl,
  now,
}) => {
  const supabase = useMemo(
    () =>
      createClient(
        "https://ighwgtdscniyuzwffnsf.supabase.co",
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlnaHdndGRzY25peXV6d2ZmbnNmIiwicm9sZSI6ImFub24iLCJpYXQiOjE2NzExMjA3MDMsImV4cCI6MTk4NjY5NjcwM30.j3ZaSHwTptB5W3lfDxYLiHOTs4Ms6ruTXo92YlxEkcU",
        {
          realtime: {
            params: {
              eventsPerSecond: 10,
            },
          },
        }
      ),
    []
  );

  const [statefulPrice, setStatefulPrice] = useState(0);
  const [auction, setAuction] = useState(auctions[0]);
  const [variant, setVariant] = useState(auctions[0].products[0]);

  const [bidAmount, setBidAmount] = useState(0);

  const [placingBid, setPlacingBid] = useState(false);
  const [confirmingBid, setConfirmingBid] = useState(false);

  const handleAuctionUpdate = useCallback(
    (realtimeUpdate) => {
      const _auction = reconcileRealtimeUpdateIntoAuction(
        auction,
        realtimeUpdate
      );
      setAuction(_auction);
    },
    [auction]
  );

  useEffect(() => {
    const realtimeSubscription = supabase
      .channel(
        `public:${AUCTIONS[auction.kind].TABLE}:external_id=eq.${
          auction.stateExternalId
        }`
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: AUCTIONS[auction.kind].TABLE,
          filter: `external_id=eq.${auction.stateExternalId}`,
        },
        (realtimeUpdate) => handleAuctionUpdate(realtimeUpdate)
      )
      .subscribe();

    async function realtimeUnsubscribe() {
      await realtimeSubscription.unsubscribe();
    }
    return () => realtimeUnsubscribe();
  }, [supabase, handleAuctionUpdate, auction, auction.stateExternalId]);

  const onPlaceBid = useCallback(
    (amount) => {
      async function executor() {
        setPlacingBid(true);
        setBidAmount(Number(amount));
      }
      executor();
    },
    [setPlacingBid, setBidAmount]
  );

  const onBuyWithCard = useCallback(() => {
    let abortController = new AbortController();
    (async () => {
      setConfirmingBid(true);

      let invoice_url = "";
      switch (auction.kind) {
        case "Ascending": {
          await placeBid({
            abortController,
            auctionId: auction.id,
            productId: variant.external_id,
            customerId,
            action: auction.kind,
            params: {
              price: bidAmount,
            },
          });
          break;
        }
        case "GDAContinuous": {
          invoice_url = await placeBid({
            abortController,
            auctionId: auction.id,
            productId: variant.external_id,
            customerId,
            action: AUCTIONS[auction.kind].ACTION,
            params: {
              quantity: bidAmount,
            },
          });
          break;
        }
        case "GDADiscrete": {
          invoice_url = await placeBid({
            abortController,
            auctionId: auction.id,
            productId: variant.external_id,
            customerId,
            action: AUCTIONS[auction.kind].ACTION,
            params: {
              quantity: bidAmount,
            },
          });
          break;
        }
        default: {
          return;
        }
      }
      if (invoice_url !== "") {
        let delay = setTimeout(() => {
          window.open(invoice_url, "_blank");
          setPlacingBid(false);
        }, 500);
        return () => clearInterval(delay);
      }
      setPlacingBid(false);
    })();

    return () => abortController?.abort();
  }, [bidAmount, auction, customerId, variant]);

  const onBack = useCallback(() => setPlacingBid(false), []);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <div
          className={`flex flex-col border-t border-opacity-30 border-black dark:border-white`}
        >
          <div className="pt-10">
            {/* TODO: Needs a selector for product variants */}
            <div className="flex items-center justify-between pt-2 mb-10">
              {placingBid && (
                <PlacingBid
                  now={now}
                  auction={auction}
                  bidAmount={bidAmount}
                  onBack={onBack}
                  confirmingBid={confirmingBid}
                  onBuyWithCard={onBuyWithCard}
                  statefulCurrentPrice={statefulPrice}
                  setStatefulCurrentPrice={setStatefulPrice}
                />
              )}
              {!placingBid && (
                <>
                  {auctionGuard &&
                    Object.keys(auctionGuard).length > 0 &&
                    !auctionGuard.settled && (
                      <Guard auctionGuard={auctionGuard} />
                    )}
                  {(Object.keys(auctionGuard).length === 0 ||
                    (Object.keys(auctionGuard).length > 0 &&
                      auctionGuard.settled)) && (
                    <PlaceBid
                      shopId={shopId}
                      productId={productId}
                      auction={auction}
                      now={now}
                      onPlaceBid={onPlaceBid}
                      customerId={customerId}
                      currentBid={0}
                      shopLoginUrl={shopLoginUrl}
                      statefulPrice={statefulPrice}
                      setStatefulPrice={setStatefulPrice}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function App({ domElement }) {
  const now = moment().unix();
  const shopIdData = domElement.getAttribute("data-shop-id");
  const shopId = Number(shopIdData);
  const productIdData = domElement.getAttribute("data-product-id");
  // shopify presents an empty string on the theme customization page.
  // present example auction from engine api.
  const productId = productIdData !== "" ? Number(productIdData) : 1;
  const customerIdData = domElement.getAttribute("data-customer-id");
  const customerId = Number(customerIdData);
  const shopLoginUrlData = domElement.getAttribute("data-shop-login-url");
  const shopLoginUrl = shopLoginUrlData;
  const [loading, setLoading] = useState(true);
  const [auctions, setAuctions] = useState([]);
  const [auctionGuard, setAuctionGuard] = useState({});

  useEffect(() => {
    let abortController = new AbortController();
    (async () => {
      setLoading(true);
      let _auctions = await getAuctionsByProductId(abortController, productId);
      if (_auctions.length > 0) {
        setAuctions(_auctions);
      }
      setLoading(false);
    })();
    return () => abortController?.abort();
  }, [productId]);

  return (
    <div>
      {!loading && auctions.length > 0 && (
        <>
          <RenderAuction
            auctionGuard={auctionGuard}
            shopId={shopId}
            productId={productId}
            auctions={auctions}
            customerId={Number(customerId)}
            shopLoginUrl={shopLoginUrl}
            now={now}
          />
        </>
      )}
    </div>
  );
}

export default App;
